import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../../environments/environment';

export interface BookMealPayload {
  mealId: string;
  StaffUserId: string;
  organizationId: string;
}

export interface CancelBookingPayload {
  StaffUserId: string;
  organizationId: string;
}

export interface RateMealPayload {
  rating: number;
  comment?: string;
}

export interface RateVendorPayload {
  bookingId: string;
  rating: number;
  note?: string;
}

@Injectable({
  providedIn: 'root',
})
export class StaffMealsService {
  private baseAPI = environment.baseAPI;

  constructor(private http: HttpClient) { }

  getStaffMenu(params: {
    OrganizationId?: string;
    CafeteriaId?: string;
    AvailableDate?: string;
    CategoryName?: string;
    DietaryTagIds?: string[];
    SearchKeyword?: string;
    PageNumber?: number;
    PageSize?: number;
  }): Observable<any> {
    let httpParams = new HttpParams();

    if (params.OrganizationId) {
      httpParams = httpParams.set('OrganizationId', params.OrganizationId);
    }
    if (params.CafeteriaId) {
      httpParams = httpParams.set('CafeteriaId', params.CafeteriaId);
    }
    if (params.AvailableDate) {
      httpParams = httpParams.set('AvailableDate', params.AvailableDate);
    }
    if (params.CategoryName) {
      httpParams = httpParams.set('CategoryName', params.CategoryName);
    }
    if (params.SearchKeyword) {
      httpParams = httpParams.set('SearchKeyword', params.SearchKeyword);
    }
    if (params.PageNumber) {
      httpParams = httpParams.set('PageNumber', params.PageNumber.toString());
    }
    if (params.PageSize) {
      httpParams = httpParams.set('PageSize', params.PageSize.toString());
    }

    if (params.DietaryTagIds && params.DietaryTagIds.length > 0) {
      params.DietaryTagIds.forEach((id) => {
        httpParams = httpParams.append('DietaryTagIds', id);
      });
    }

    return this.http.get(`${this.baseAPI}/StaffMeal/staff/menu`, { params: httpParams });
  }

  bookMeal(payload: BookMealPayload): Observable<any> {
    return this.http.post(`${this.baseAPI}/staff/meals/book`, payload);
  }

  getBookingHistory(params: {
    StaffUserId?: string;
    OrganizationId?: string;
    StatusFilter?: string;
    PageNumber?: number;
    PageSize?: number;
  }): Observable<any> {
    let httpParams = new HttpParams();

    if (params.StaffUserId) {
      httpParams = httpParams.set('StaffUserId', params.StaffUserId);
    }
    if (params.OrganizationId) {
      httpParams = httpParams.set('OrganizationId', params.OrganizationId);
    }
    if (params.StatusFilter) {
      httpParams = httpParams.set('StatusFilter', params.StatusFilter);
    }
    if (params.PageNumber) {
      httpParams = httpParams.set('PageNumber', params.PageNumber.toString());
    }
    if (params.PageSize) {
      httpParams = httpParams.set('PageSize', params.PageSize.toString());
    }

    return this.http.get(`${this.baseAPI}/staff/meals/bookings/history`, { params: httpParams });
  }

  cancelBooking(bookingId: string, payload: CancelBookingPayload): Observable<any> {
    return this.http.delete(`${this.baseAPI}/staff/meals/bookings/${bookingId}/cancel`, {
      body: payload,
    });
  }

  getCafeterias(
    organizationId: string,
    pageNumber: number = 1,
    pageSize: number = 100,
    searchKeyword?: string,
    status?: string,
    startDate?: string,
    endDate?: string,
  ): Observable<any> {
    let params = new HttpParams()
      .set('PageNumber', pageNumber.toString())
      .set('PageSize', pageSize.toString());

    if (searchKeyword) {
      params = params.set('SearchKeyword', searchKeyword);
    }
    if (status) {
      params = params.set('status', status);
    }
    if (startDate) {
      params = params.set('StartDate', startDate);
    }
    if (endDate) {
      params = params.set('EndDate', endDate);
    }

    return this.http.get(`${this.baseAPI}/StaffMeal/organisations/${organizationId}/cafeterias`, { params });
  }

  getDailyMenu(params: {
    StaffUserId: string;
    CafeteriaId: string;
    TargetDate: string;
    MealCategoryConfigId?: string;
    DietaryTagIds?: string[];
  }): Observable<any> {
    let httpParams = new HttpParams()
      .set('StaffUserId', params.StaffUserId)
      .set('CafeteriaId', params.CafeteriaId)
      .set('TargetDate', params.TargetDate);

    if (params.MealCategoryConfigId) {
      httpParams = httpParams.set('MealCategoryConfigId', params.MealCategoryConfigId);
    }

    if (params.DietaryTagIds && params.DietaryTagIds.length > 0) {
      params.DietaryTagIds.forEach((id) => {
        httpParams = httpParams.append('DietaryTagIds', id);
      });
    }

    return this.http.get(`${this.baseAPI}/StaffMeal/staff/daily-menu`, { params: httpParams });
  }

  getMealCategoryConfigs(organizationId: string): Observable<any> {
    return this.http.get(`${this.baseAPI}/StaffMeal/organization/${organizationId}/meal-categories`);
  }

  getDietaryTags(organizationId: string): Observable<any> {
    return this.http.get(`${this.baseAPI}/StaffMeal/organization/${organizationId}/dietary-tags`);
  }

  validateQrCode(payload: { qrCodePayload: string }): Observable<any> {
    return this.http.post(`${this.baseAPI}/staff/meals/validate-qr`, payload);
  }

  getProfile(): Observable<any> {
    return this.http.get(`${this.baseAPI}/staff/profile/me`);
  }

  updateProfile(payload: any): Observable<any> {
    return this.http.put(`${this.baseAPI}/staff/profile/me`, payload);
  }

  getDietaryPreferences(): Observable<any> {
    return this.http.get(`${this.baseAPI}/staff/profile/me/dietary-preferences`);
  }

  updateDietaryPreferences(payload: { dietaryTagIds: string[] }): Observable<any> {
    return this.http.put(`${this.baseAPI}/staff/profile/me/dietary-preferences`, payload);
  }

  getNotificationPreferences(): Observable<any> {
    return this.http.get(`${this.baseAPI}/staff/profile/me/notification-preferences`);
  }

  getOrganisations(): Observable<any> {
    return this.http.get(`${this.baseAPI}/Organisations`);
  }

  updateNotificationPreferences(payload: { isEmailEnabled: boolean; isPushNotificationEnabled: boolean; isSmsEnabled: boolean }): Observable<any> {
    return this.http.put(`${this.baseAPI}/staff/profile/me/notification-preferences`, payload);
  }

  rateMeal(bookingId: string, payload: RateMealPayload): Observable<any> {
    return this.http.post(`${this.baseAPI}/staff/meals/bookings/${bookingId}/rate`, payload);
  }

  getMealRating(bookingId: string): Observable<any> {
    return this.http.get(`${this.baseAPI}/staff/meals/bookings/${bookingId}/rating`);
  }

  rateVendor(payload: RateVendorPayload): Observable<any> {
    return this.http.post(`${this.baseAPI}/vendors/ratings`, payload);
  }

  getVendorRating(bookingId: string): Observable<any> {
    return this.http.get(`${this.baseAPI}/vendors/ratings/${bookingId}`);
  }

  getPendingRatings(): Observable<any> {
    return this.http.get(`${this.baseAPI}/staff/meals/bookings/pending-ratings`);
  }
}
